"use client";

import { useRef, useState, type FormEvent } from "react";
import type { CapacityInputs, CapacityResult } from "@/lib/capacity-check/model";
import { CONSENT_TEXT, buildRequest, sendRequest, type ContactFields } from "@/lib/capacity-check/request";

/**
 * "Request a DepotTwin study" form under the Depot Capacity Check.
 *
 * Sends the visitor's details, inputs and estimates to AmpVerve's CRM through
 * lib/capacity-check/request.ts, only when they press Send and only with the
 * consent box ticked. If that fails, the email link (the page's mailto, with
 * the results in the body) is offered instead. The hidden "website" field is a
 * honeypot for form filling bots.
 */

const input =
  "w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-[14px] text-slate-950 outline-none focus:ring-2 focus:ring-accent/60";

const EMPTY: ContactFields = { company: "", name: "", email: "", depotPostcode: "", consent: false, website: "" };

export default function CapacityCheckRequest({
  inputs,
  result,
  mailto,
}: {
  inputs: CapacityInputs;
  result: CapacityResult;
  mailto: string;
}) {
  const startedAt = useRef<number>(Date.now());
  const [fields, setFields] = useState<ContactFields>(EMPTY);
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [message, setMessage] = useState("");
  const [emailInstead, setEmailInstead] = useState(false);

  const set = (key: keyof ContactFields) => (e: { target: { value: string } }) =>
    setFields((prev) => ({ ...prev, [key]: e.target.value }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (state === "sending") return;
    if (!fields.consent) {
      setState("failed");
      setMessage("Tick the box so we can reply to you.");
      return;
    }
    setState("sending");
    setMessage("");
    const outcome = await sendRequest(buildRequest(fields, inputs, result, startedAt.current));
    if (outcome.ok) {
      setState("sent");
      setMessage(outcome.message);
      return;
    }
    setState("failed");
    setMessage(outcome.message);
    if (!outcome.retry) setEmailInstead(true);
  };

  if (state === "sent") {
    return (
      <section id="cc-request" className="grid gap-2 rounded-xl border border-emerald-400 bg-surface p-5 shadow-card" role="status">
        <h2 className="m-0 font-display text-[18px] font-bold">Request received</h2>
        <p className="m-0 max-w-[64ch] text-[14px]">{message}</p>
        <p className="m-0 max-w-[64ch] text-[13px] text-muted">
          We have your results for {result.params.n} vans on {Math.round(result.params.cap)} kW and your depot postcode.
          If you have them, your charge point session export and a recent electricity bill help the study.
        </p>
      </section>
    );
  }

  return (
    <section id="cc-request" className="grid gap-3 rounded-xl border border-border bg-surface p-5 shadow-card">
      <h2 className="m-0 font-display text-[18px] font-bold">Request a DepotTwin study</h2>
      <p className="m-0 max-w-[64ch] text-[14px] text-muted">
        Send us these results and a person will reply by email. Nothing is sent until you press Send, and nothing is
        emailed to you automatically.
      </p>
      <form noValidate onSubmit={onSubmit} className="grid gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="grid gap-1 text-[13px] font-medium">
            Company
            <input required maxLength={200} autoComplete="organization" value={fields.company} onChange={set("company")} className={input} />
          </label>
          <label className="grid gap-1 text-[13px] font-medium">
            Your name
            <input required maxLength={120} autoComplete="name" value={fields.name} onChange={set("name")} className={input} />
          </label>
          <label className="grid gap-1 text-[13px] font-medium">
            Work email
            <input required type="email" maxLength={254} autoComplete="email" value={fields.email} onChange={set("email")} className={input} />
          </label>
          <label className="grid gap-1 text-[13px] font-medium">
            Depot postcode
            <input required maxLength={10} autoComplete="postal-code" value={fields.depotPostcode} onChange={set("depotPostcode")} className={input} />
          </label>
        </div>
        {/* Honeypot: off screen and out of the tab order, so only a bot fills it. */}
        <div aria-hidden="true" className="absolute -left-[10000px] h-px w-px overflow-hidden">
          <label>
            Website
            <input tabIndex={-1} autoComplete="off" value={fields.website} onChange={set("website")} />
          </label>
        </div>
        <label className="flex items-start gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={fields.consent}
            onChange={(e) => setFields((prev) => ({ ...prev, consent: e.target.checked }))}
            className="mt-0.5 h-4 w-4 accent-emerald-600"
          />
          <span>
            {CONSENT_TEXT}{" "}
            <a href="https://www.ampverve.com/privacy-policy/" target="_blank" rel="noreferrer" className="underline">
              Privacy policy
            </a>
          </span>
        </label>
        <div className="flex flex-wrap items-center gap-2.5">
          <button
            type="submit"
            disabled={state === "sending"}
            className="inline-flex items-center rounded-md bg-accent px-4 py-2.5 text-[14px] font-semibold text-accent-foreground transition hover:opacity-90 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {state === "sending" ? "Sending..." : "Send"}
          </button>
          <a href={mailto} className="text-[13px] underline">
            Prefer email? Send your results from your own mail client
          </a>
        </div>
        {state === "failed" && message ? (
          <div role="alert" className="grid gap-1.5 rounded-md border border-red-400 p-3 text-[13px]">
            <p className="m-0">{message}</p>
            {emailInstead ? (
              <a
                href={mailto}
                className="inline-flex w-fit items-center rounded-md bg-slate-950 px-3 py-2 text-[13px] font-semibold text-slate-100"
              >
                Email your results to us
              </a>
            ) : null}
          </div>
        ) : null}
      </form>
    </section>
  );
}
